/**
 * The names pinned on a page's map, each with a × that leaves it (FlightConnections' "From" and "To"): a port page pins
 * its port, whose × closes the page; a route page pins its two ends, whose × drops that end and opens the other's port
 * page. The map is the page on a phone (map mode), where the panel and its close button are folded away.
 */

import { nearLng } from '../lib/geo';
import { shortWay } from './frame';

export interface Pin {
  slug: string;
  name: string;
  coordinates: [number, number];
  /** The side of the label the port is on (MapLibre's marker `anchor`): the label points into the map, not off its edge. */
  anchor: 'left' | 'right';
  /** The port page its × opens, its slug; none for the home page. */
  closesTo?: string;
}

/** A port feature as the page's GeoJSON holds it: a point. */
interface PortFeature {
  geometry: { type: string; coordinates?: unknown };
  properties: Record<string, unknown> | null;
}

/** The pins of a page's map: none on the home page or an operator's. */
export function pinsOf(data: { ports: { features: PortFeature[] }; mainPortId?: number; page?: string } | null): Pin[] {
  if (!data) return [];
  const ports = data.ports.features.filter((feature) => feature.geometry.type === 'Point');
  // A port page's port; a route page's ends, the ports it does not mark as called at on the way or beyond the trip (`kind`)
  const pinned =
    data.mainPortId !== undefined
      ? ports.filter((feature) => feature.properties?.id === data.mainPortId)
      : data.page
        ? ports.filter((feature) => !feature.properties?.kind)
        : [];
  // Each label points into the map, not off its edge: toward the middle, east to west, of two ends, else of all the ports,
  // framed the short way round (across the dateline for ports either side of it)
  const point = (feature: PortFeature) => feature.geometry.coordinates as [number, number];
  const lngs = shortWay((pinned.length === 2 ? pinned : ports).map(point)).map(([lng]) => lng);
  const middle = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const lng = (feature: PortFeature) => nearLng(point(feature)[0], middle);
  return pinned.map((feature) => {
    // A route's end drops itself and opens the other end; a lone pin closes the page
    const other = pinned.length === 2 ? pinned.find((end) => end !== feature) : undefined;
    return {
      slug: String(feature.properties?.slug ?? ''),
      name: String(feature.properties?.name ?? ''),
      coordinates: point(feature),
      anchor: lng(feature) > middle ? 'right' : 'left',
      ...(other && { closesTo: String(other.properties?.slug) }),
    };
  });
}
